import { getAuth } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, doc, getDoc } from "@/firebase";

const functions = getFunctions();

// Type definitions for Firebase function responses
interface DeviceCountResponse {
  currentDeviceCount: number;
  maxDevices: number;
  canAddMore: boolean;
}

interface AddDeviceResponse {
  success: boolean;
  message: string;
  isNew: boolean;
  deviceLimitReached?: boolean;
  currentDeviceCount?: number;
  maxDevices?: number;
}

interface TrustedDevicesResponse {
  trustedDevices: TrustedDevice[];
}

interface RemoveDeviceResponse {
  message: string;
}

interface VerifyDeviceResponse {
  isTrusted: boolean;
  message: string;
  deviceName?: string;
}

export interface TrustedDevice {
  id: string;
  deviceFingerprint: string;
  deviceName: string;
  browserInfo: string;
  osInfo: string;
  ipAddress: string;
  location: string;
  lastUsed: any;
  createdAt: any;
  expiresAt: any;
  isExpired: boolean;
}

export interface DeviceInfo {
  fingerprint: string;
  name: string;
  browserInfo: string;
  osInfo: string;
  ipAddress: string;
  location?: string;
}

class TrustedDeviceService {
  private getCurrentUser() {
    const auth = getAuth();
    return auth.currentUser;
  }

  private async getFirebaseToken(): Promise<string> {
    const user = this.getCurrentUser();
    if (!user) {
      throw new Error("User not authenticated");
    }
    return await user.getIdToken();
  }

  /**
   * Check if current user is an admin
   */
  private async isCurrentUserAdmin(): Promise<boolean> {
    try {
      const user = this.getCurrentUser();
      if (!user) {
        return false;
      }

      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        return userData.userType === "admin" || userData.userType === "superadmin";
      }
      return false;
    } catch (error) {
      console.error("Error checking user role:", error);
      return false;
    }
  }

  /**
   * Generate device fingerprint
   * Made less sensitive by excluding screen size and canvas (which change with DevTools)
   * Version 2: More stable fingerprint that doesn't change with DevTools
   */
  generateDeviceFingerprint(): string {
    const fingerprint = [
      "v2", // Version prefix to force re-fingerprinting when algorithm changes
      navigator.userAgent,
      navigator.language,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      // Removed: screen.width + "x" + screen.height (changes when DevTools opens)
      // Removed: canvas.toDataURL() (changes with DevTools)
    ].join("|");

    // Simple hash function
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Get device information
   */
  getDeviceInfo(): DeviceInfo {
    const fingerprint = this.generateDeviceFingerprint();

    // Parse browser info from user agent
    const userAgent = navigator.userAgent;
    let browserInfo = "Unknown Browser";
    let osInfo = "Unknown OS";

    // Browser detection - Order matters! Check most specific first
    // Firefox and Edge must be checked before Chrome because they may contain "Chrome" in their UA
    if (userAgent.includes("Edg")) {
      // Edge uses "Edg" or "Edge" in user agent
      browserInfo = "Edge";
    } else if (userAgent.includes("Firefox")) {
      browserInfo = "Firefox";
    } else if (userAgent.includes("Chrome")) {
      // Chrome check must come after Firefox/Edge
      browserInfo = "Chrome";
    } else if (userAgent.includes("Safari")) {
      // Safari check last since Chrome also contains "Safari"
      browserInfo = "Safari";
    }

    // OS detection
    if (userAgent.includes("Windows")) {
      osInfo = "Windows";
    } else if (userAgent.includes("Mac")) {
      osInfo = "macOS";
    } else if (userAgent.includes("Linux")) {
      osInfo = "Linux";
    } else if (userAgent.includes("Android")) {
      osInfo = "Android";
    } else if (userAgent.includes("iOS")) {
      osInfo = "iOS";
    }

    return {
      fingerprint,
      name: `${browserInfo} on ${osInfo}`,
      browserInfo,
      osInfo,
      ipAddress: "Unknown", // Will be determined server-side
      location: "Unknown",
    };
  }

  /**
   * Get trusted device count
   */
  async getTrustedDeviceCount(): Promise<{
    currentDeviceCount: number;
    maxDevices: number;
    canAddMore: boolean;
  }> {
    try {
      // Check if user is admin before proceeding
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        return {
          currentDeviceCount: 0,
          maxDevices: 0,
          canAddMore: false,
        };
      }

      const getTrustedDeviceCount = httpsCallable<{}, DeviceCountResponse>(
        functions,
        "getTrustedDeviceCount"
      );
      const result = await getTrustedDeviceCount();
      const data = result.data;

      return {
        currentDeviceCount: data.currentDeviceCount || 0,
        maxDevices: data.maxDevices || 3,
        canAddMore: data.canAddMore || false,
      };
    } catch (error: any) {
      console.error("Error getting trusted device count:", error);
      throw new Error(error.message || "Failed to get device count");
    }
  }

  /**
   * Add current device as trusted
   */
  async addTrustedDevice(
    deviceName?: string,
    expirationDays: number = 90
  ): Promise<{
    success: boolean;
    message: string;
    isNew: boolean;
    deviceLimitReached?: boolean;
    currentDeviceCount?: number;
    maxDevices?: number;
  }> {
    try {
      // Check if user is admin before proceeding
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        return {
          success: false,
          message: "Only admins can add trusted devices",
          isNew: false,
        };
      }

      const addTrustedDevice = httpsCallable<any, AddDeviceResponse>(functions, "addTrustedDevice");
      const deviceInfo = this.getDeviceInfo();

      const result = await addTrustedDevice({
        deviceFingerprint: deviceInfo.fingerprint,
        deviceName: deviceName || deviceInfo.name,
        browserInfo: deviceInfo.browserInfo,
        osInfo: deviceInfo.osInfo,
        ipAddress: deviceInfo.ipAddress,
        location: deviceInfo.location,
        expirationDays,
      });

      const data = result.data;
      return {
        success: data.success || true,
        message: data.message || "Device added as trusted",
        isNew: data.isNew || false,
        deviceLimitReached: data.deviceLimitReached || false,
        currentDeviceCount: data.currentDeviceCount,
        maxDevices: data.maxDevices,
      };
    } catch (error: any) {
      console.error("Error adding trusted device:", error);

      // Check if it's a device limit error
      if (
        error.code === "functions/failed-precondition" ||
        error.message?.includes("Maximum of 3 trusted devices")
      ) {
        return {
          success: false,
          message: "Maximum of 3 trusted devices allowed. Please remove an existing device first.",
          isNew: false,
          deviceLimitReached: true,
          currentDeviceCount: 3,
          maxDevices: 3,
        };
      }

      throw new Error(error.message || "Failed to add trusted device");
    }
  }

  /**
   * Get all trusted devices for current user
   */
  async getTrustedDevices(): Promise<TrustedDevice[]> {
    try {
      // Check if user is admin before proceeding
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        return [];
      }

      const getTrustedDevices = httpsCallable<{}, TrustedDevicesResponse>(
        functions,
        "getTrustedDevices"
      );
      const result = await getTrustedDevices();
      const data = result.data;

      return data.trustedDevices || [];
    } catch (error: any) {
      console.error("Error getting trusted devices:", error);
      throw new Error(error.message || "Failed to get trusted devices");
    }
  }

  /**
   * Remove a trusted device
   */
  async removeTrustedDevice(deviceId: string): Promise<{ success: boolean; message: string }> {
    try {
      // Check if user is admin before proceeding
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        return {
          success: false,
          message: "Only admins can remove trusted devices",
        };
      }

      const removeTrustedDevice = httpsCallable<{ deviceId: string }, RemoveDeviceResponse>(
        functions,
        "removeTrustedDevice"
      );

      const result = await removeTrustedDevice({
        deviceId,
      });

      const data = result.data;
      return {
        success: true,
        message: data.message || "Device removed successfully",
      };
    } catch (error: any) {
      console.error("Error removing trusted device:", error);
      throw new Error(error.message || "Failed to remove trusted device");
    }
  }

  /**
   * Verify if current device is trusted
   */
  async verifyTrustedDevice(): Promise<{
    isTrusted: boolean;
    message: string;
    deviceName?: string;
  }> {
    try {
      // Ensure user is signed in before calling the Cloud Function
      const currentUser = this.getCurrentUser();
      if (!currentUser) {
        return {
          isTrusted: false,
          message: "User not authenticated",
        };
      }

      // Check if user is admin before proceeding
      const isAdmin = await this.isCurrentUserAdmin();
      if (!isAdmin) {
        return {
          isTrusted: false,
          message: "Only admins can use trusted devices",
        };
      }

      const verifyTrustedDevice = httpsCallable<
        { deviceFingerprint: string },
        VerifyDeviceResponse
      >(functions, "verifyTrustedDevice");
      const deviceInfo = this.getDeviceInfo();

      const result = await verifyTrustedDevice({
        deviceFingerprint: deviceInfo.fingerprint,
      });

      const data = result.data;
      return {
        isTrusted: data.isTrusted || false,
        message: data.message || "Device verification completed",
        deviceName: data.deviceName,
      };
    } catch (error: any) {
      console.error("Error verifying trusted device:", error);
      return {
        isTrusted: false,
        message: "Failed to verify device",
      };
    }
  }

  /**
   * Check if current device is trusted (cached result)
   * Uses sessionStorage to bypass check once verified in current session
   */
  async isCurrentDeviceTrusted(): Promise<boolean> {
    try {
      // Check if device was already verified in this session
      const sessionVerified = sessionStorage.getItem("deviceVerifiedThisSession");
      if (sessionVerified === "true") {
        return true;
      }

      const verification = await this.verifyTrustedDevice();
      
      // If trusted, mark as verified for this session
      if (verification.isTrusted) {
        sessionStorage.setItem("deviceVerifiedThisSession", "true");
      }
      
      return verification.isTrusted;
    } catch (error) {
      console.error("Error checking if device is trusted:", error);
      return false;
    }
  }

  /**
   * Store trusted device status in localStorage and sessionStorage
   */
  setDeviceTrusted(isTrusted: boolean, deviceName?: string) {
    const deviceInfo = this.getDeviceInfo();
    localStorage.setItem(
      "deviceTrusted",
      JSON.stringify({
        isTrusted,
        deviceName,
        fingerprint: deviceInfo.fingerprint,
        timestamp: Date.now(),
      })
    );
    
    // Mark as verified for this session
    if (isTrusted) {
      sessionStorage.setItem("deviceVerifiedThisSession", "true");
    }
  }

  /**
   * Get cached trusted device status
   */
  getCachedTrustedStatus(): { isTrusted: boolean; deviceName?: string; timestamp: number } | null {
    try {
      const cached = localStorage.getItem("deviceTrusted");
      if (cached) {
        const data = JSON.parse(cached);
        const deviceInfo = this.getDeviceInfo();

        // Check if fingerprint matches and cache is not too old (24 hours)
        if (
          data.fingerprint === deviceInfo.fingerprint &&
          Date.now() - data.timestamp < 24 * 60 * 60 * 1000
        ) {
          return data;
        } else {
          // Remove outdated cache
          localStorage.removeItem("deviceTrusted");
        }
      }
    } catch (error) {
      console.error("Error getting cached trusted status:", error);
    }
    return null;
  }
}

// Lazy singleton instance to avoid circular dependency issues
let trustedDeviceServiceInstance: TrustedDeviceService | null = null;

export function getTrustedDeviceService(): TrustedDeviceService {
  if (!trustedDeviceServiceInstance) {
    trustedDeviceServiceInstance = new TrustedDeviceService();
  }
  return trustedDeviceServiceInstance;
}

// For backward compatibility, export a proxy that lazily initializes
export const trustedDeviceService = new Proxy({} as TrustedDeviceService, {
  get(target, prop) {
    return getTrustedDeviceService()[prop as keyof TrustedDeviceService];
  }
});
